/**
 * Centralized configuration for the recommendation system.
 *
 * Adding a new signal type or prepared action type means adding one entry
 * here — UI rendering, action execution, and label formatting all derive
 * from these registries.
 */

import type { LucideIcon } from 'lucide-react'
import { Mail, Snowflake, ArrowRight, CalendarClock, Bell } from 'lucide-react'
import type {
  SignalType,
  EnrichedAction,
  PreparedAction,
} from '../../services/recommendations_contracts'
import {
  applyPreparedAction,
  applyDealUpdate,
  applyReminderUpdate,
  deleteReminder,
} from '../../services/recommendations_api'

// ---------------------------------------------------------------------------
// Signal Type Configuration
// ---------------------------------------------------------------------------

export interface SignalTypeConfig {
  /** Display title for section headers (plural, e.g. "Unreplied Emails") */
  title: string
  /** Singular form for individual card labels (e.g. "Unreplied Email") */
  singularTitle: string
  /** Description shown in section tooltips */
  description: string
  /** Tooltip content (may differ from description) */
  tooltip: string
  /** User-facing description for the AI settings page */
  settingsDescription: string
  /** Lucide icon for the signal type */
  icon: LucideIcon
  /** Link target: 'deal' always links to deal, 'company' always to company,
   *  'auto' links to deal if deal_id exists, else company */
  linkTarget: 'deal' | 'company' | 'auto'
  /** Format the days-based label for this signal */
  formatLabel: (action: EnrichedAction) => string
}

// Insertion order determines the Signal view tab order in the UI.
// Do not reorder entries without updating the tab layout.
export const SIGNAL_CONFIG: Record<SignalType, SignalTypeConfig> = {
  unreplied_email: {
    title: 'Unreplied Emails',
    singularTitle: 'Unreplied Email',
    description: 'Inbound messages from 3-60 days ago awaiting your response',
    tooltip:
      'Inbound emails from 3-60 days ago awaiting your response. Automated emails (shipping, receipts, etc.) are filtered out.',
    settingsDescription:
      'Surfaces inbound emails that haven\u2019t been followed up on, so reps never miss a lead or buyer reaching out.',
    icon: Mail,
    linkTarget: 'company',
    formatLabel: (a) => `${a.days_unreplied ?? 0}d unreplied`,
  },
  inactive_deal: {
    title: 'Inactive Deals',
    singularTitle: 'Inactive Deal',
    description:
      'Deals needing attention (7-90 days stale or 14+ days in same stage)',
    tooltip:
      'Deals with no activity in 7+ days OR stuck in the same stage for 14+ days. Limited to deals active within the last 90 days.',
    settingsDescription:
      'Flags open deals that have gone quiet, prompting reps to re-engage before momentum is lost.',
    icon: Snowflake,
    linkTarget: 'deal',
    formatLabel: (a) => {
      if (a.days_in_stage && a.days_in_stage >= 14) {
        return `${a.days_in_stage}d in ${a.current_stage || 'stage'}`
      }
      return `${a.days_stale ?? 0}d inactive`
    },
  },
  email_stage_change: {
    title: 'Update Deal',
    singularTitle: 'Update Deal',
    description: 'Deals where recent activity suggests updates are needed',
    tooltip:
      'Recent inbound email suggests deal fields need updating — stage, close date, or other details.',
    settingsDescription:
      'Spots inbound emails that signal a deal needs updating and lets reps advance the stage, update close date, or add a note in one click.',
    icon: ArrowRight,
    linkTarget: 'deal',
    formatLabel: (a) =>
      `${a.days_in_stage ?? 0}d in ${a.current_stage || 'stage'}`,
  },
  schedule_follow_up: {
    title: 'Schedule Follow-Up',
    singularTitle: 'Schedule Follow-Up',
    description: 'Emails where a follow-up action is pending',
    tooltip:
      'An email contains a commitment or request that needs follow-through. Set a reminder to stay on top of it.',
    settingsDescription:
      'Identifies inbound requests and outbound promises in your emails and suggests follow-up reminders.',
    icon: CalendarClock,
    linkTarget: 'company',
    formatLabel: (a) => `${a.days_since_email ?? 0}d ago`,
  },
  overdue_reminder: {
    title: 'Reminders',
    singularTitle: 'Reminder',
    description: 'Upcoming and overdue follow-up reminders',
    tooltip: 'Reminders that are due or overdue. Reschedule or act on them.',
    settingsDescription:
      'Surfaces upcoming and overdue reminders so reps never miss a follow-up.',
    icon: Bell,
    linkTarget: 'auto',
    formatLabel: (a) => {
      const d = a.days_overdue
      if (d == null) return 'Due soon'
      if (d > 0) return `${d}d overdue`
      if (d === 0) return 'Due today'
      return `Due in ${Math.abs(d)}d`
    },
  },
}

// ---------------------------------------------------------------------------
// Prepared Action Executors
// ---------------------------------------------------------------------------

export interface CreateReminderPayload {
  title: string
  run_at: string
  recipients: number[]
  deals?: number[]
  contact_company?: number
}

/** API surface needed by action executors (subset of useApiService return). */
export interface ActionApi {
  createDealNote: (
    dealId: number,
    content: string,
    attachments?: File[]
  ) => Promise<unknown>
  createReminder: (data: CreateReminderPayload) => Promise<unknown>
  currentUserId: number
}

export type ActionExecutor = (
  dealId: number,
  pa: PreparedAction,
  api: ActionApi
) => Promise<void>

export const ACTION_EXECUTORS: Record<string, ActionExecutor> = {
  update_stage: async (dealId, pa) => {
    if (pa.type !== 'update_stage') return
    await applyPreparedAction(dealId, pa.target_stage_id)
  },
  update_close_date: async (dealId, pa) => {
    if (pa.type !== 'update_close_date') return
    await applyDealUpdate(dealId, {
      close_date_override: pa.target_close_date,
    })
  },
  create_reminder: async (dealId, pa, api) => {
    if (pa.type !== 'create_reminder') return
    await api.createReminder({
      title: pa.reminder_title,
      run_at: `${pa.reminder_date}T09:00:00Z`,
      recipients: [api.currentUserId],
      ...(pa.deal_id ? { deals: [pa.deal_id] } : {}),
      ...(pa.contact_company_id
        ? { contact_company: pa.contact_company_id }
        : {}),
    })
  },
  reschedule_reminder: async (dealId, pa) => {
    if (pa.type !== 'reschedule_reminder') return
    await applyReminderUpdate(pa.reminder_id, {
      run_at: `${pa.target_date}T09:00:00Z`,
    })
  },
  delete_reminder: async (dealId, pa) => {
    if (pa.type !== 'delete_reminder') return
    await deleteReminder(pa.reminder_id)
  },
  add_note: async (dealId, pa, api) => {
    if (pa.type !== 'add_note') return
    await api.createDealNote(dealId, pa.draft_content, pa.attachments)
  },
}

/**
 * Execute a single prepared action by dispatching to the appropriate executor.
 *
 * @throws Re-throws any API errors (callers handle toast + bail-out).
 */
export async function executePreparedAction(
  dealId: number,
  pa: PreparedAction,
  api: ActionApi
): Promise<void> {
  const executor = ACTION_EXECUTORS[pa.type]
  if (!executor) {
    console.warn(`No executor registered for prepared action type: ${pa.type}`)
    return
  }
  await executor(dealId, pa, api)
}
